import type { ChatSiderComponent } from "./_types";

import { memo, forwardRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Conversations } from "@ant-design/x";
import { Drawer, Button, Empty, Input, Tooltip } from "antd";

import { default as classes } from "./_.module.scss";
import { useChatSider } from "./_hooks";

import { ConversationListItem, DATE } from "~/common/contexts/knowledge";
import { AntdIcon } from "~/libs/antd/icon";

/**
 * ChatSider Component View
 */
export default memo(
  forwardRef((props, ref) => {
    /** useHooks */
    const {
      open,
      conversationItems,
      curConversationId,
      handleClose,
      handleActiveChangeWithClose,
      handleAddConversationWithClose,
      handleDeleteConversation,
      editingId,
      editingValue,
      handleOpenRenameEdit,
      handleConfirmRename,
      handleCancelRename,
      setEditingValue,
    } = useChatSider({ ref, ...props });

    const { getContainer } = props;

    /** i18n */
    const { t } = useTranslation("common");

    const parseDate = useCallback((value?: string) => {
      if (!value) return undefined;
      const date = new Date(value);
      return date;
    }, []);

    const formatDate = useCallback(
      (date: Date, options?: { includeTime?: boolean }) => {
        const { includeTime = false } = options ?? {};
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        if (!includeTime) {
          return `${year}-${month}-${day}`;
        }

        const hours = `${date.getHours()}`.padStart(2, "0");
        const minutes = `${date.getMinutes()}`.padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      },
      [],
    );

    const formatTooltipDate = useCallback(
      (value?: string) => {
        const date = parseDate(value);
        if (!date) {
          return t("chat.sidebar.lastActivityUnknown");
        }

        return formatDate(date, { includeTime: true });
      },
      [formatDate, parseDate, t],
    );

    const getGroupLabel = useCallback(
      (group?: string) => {
        if (group === DATE.TODAY) {
          return (
            <div className="flex gap-1">
              <AntdIcon name="FieldTimeOutlined" />
              {t("chat.sidebar.group.today")}
            </div>
          );
        }

        if (group === DATE.YESTERDAY) {
          return (
            <div className="flex gap-1">
              <AntdIcon name="FieldTimeOutlined" />
              {t("chat.sidebar.group.yesterday")}
            </div>
          );
        }

        if (group === DATE.LAST_7_DAYS) {
          return (
            <div className="flex gap-1">
              <AntdIcon name="FieldTimeOutlined" />
              {t("chat.sidebar.group.last7Days")}
            </div>
          );
        }

        if (group === DATE.LAST_30_DAYS) {
          return (
            <div className="flex gap-1">
              <AntdIcon name="FieldTimeOutlined" />
              {t("chat.sidebar.group.last30Days")}
            </div>
          );
        }

        // Handle month format: "month:YYYY-MM"
        if (group?.startsWith("month:")) {
          const monthValue = group.replace("month:", "");
          const [year, month] = monthValue.split("-");
          if (year && month) {
            // Create date for the first day of that month
            const date = new Date(parseInt(year), parseInt(month) - 1, 1);
            // Format as "January 2025" using local locale
            return (
              <div className="flex gap-1">
                <AntdIcon name="FieldTimeOutlined" />
                {date.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                })}
              </div>
            );
          }
        }

        return t("chat.sidebar.group.earlier");
      },
      [t],
    );

    /** Sort conversations by last activity date */
    const sortedConversations = useMemo(() => {
      if (!conversationItems || conversationItems.length === 0) {
        return [] as ConversationListItem[];
      }

      return [...conversationItems].sort((a, b) => {
        const dateA = parseDate(a.lastActivityAt)?.getTime() ?? 0;
        const dateB = parseDate(b.lastActivityAt)?.getTime() ?? 0;
        return dateB - dateA;
      });
    }, [conversationItems, parseDate]);

    const renderedConversationItems = useMemo(
      () =>
        sortedConversations.map((item) => {
          const { label: itemLabel, ...rest } = item;

          if (editingId === item.key) {
            return {
              ...rest,
              label: (
                <div
                  className={classes.editingItem}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Input
                    size="small"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={handleConfirmRename}
                    onPressEnter={handleConfirmRename}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        handleCancelRename();
                      }
                    }}
                    autoFocus
                    className={classes.editInput}
                  />
                </div>
              ),
            };
          }

          return {
            ...rest,
            label: (
              <Tooltip
                title={formatTooltipDate(item.lastActivityAt)}
                placement="right"
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              >
                <span>{itemLabel}</span>
              </Tooltip>
            ),
          };
        }),
      [
        editingId,
        editingValue,
        formatTooltipDate,
        handleCancelRename,
        handleConfirmRename,
        setEditingValue,
        sortedConversations,
      ],
    );

    /**
     * Render sidebar content
     */
    const renderContent = useMemo(
      () => (
        <div className={classes.siderContent}>
          <Button
            type="primary"
            block
            icon={<AntdIcon name="FormOutlined" />}
            onClick={handleAddConversationWithClose}
            className={classes.addConversationButton}
          >
            {t("chat.sidebar.addNew")}
          </Button>

          {/* Conversations List */}
          <div className={classes.conversationsList}>
            {renderedConversationItems.length > 0 ? (
              <Conversations
                items={renderedConversationItems}
                className={classes.conversations}
                activeKey={curConversationId}
                onActiveChange={handleActiveChangeWithClose}
                styles={{ item: { padding: "0 8px" } }}
                groupable={{ label: (group) => getGroupLabel(group) }}
                menu={(item) =>
                  editingId === item.key
                    ? { items: [] }
                    : {
                        items: [
                          {
                            label: t("chat.sidebar.rename"),
                            key: "rename",
                            icon: (
                              <AntdIcon
                                name="EditOutlined"
                                style={{ fontSize: "16px" }}
                              />
                            ),
                            className: classes.conversationItem,
                            onClick: () => {
                              handleOpenRenameEdit(item.key);
                            },
                          },
                          {
                            label: t("chat.sidebar.delete"),
                            key: "delete",
                            icon: (
                              <AntdIcon
                                name="DeleteOutlined"
                                style={{ fontSize: "16px", color: "red" }}
                              />
                            ),
                            className: classes.conversationItem,
                            onClick: () => {
                              handleDeleteConversation(item.key);
                            },
                          },
                        ],
                        className: classes.conversationMenu,
                        getPopupContainer: (trigger) =>
                          trigger.parentElement || document.body,
                      }
                }
              />
            ) : (
              <Empty
                description={t("chat.sidebar.empty")}
                style={{ marginTop: 32 }}
              />
            )}
          </div>
        </div>
      ),
      [
        renderedConversationItems,
        editingId,
        curConversationId,
        t,
        handleAddConversationWithClose,
        handleActiveChangeWithClose,
        handleDeleteConversation,
        handleOpenRenameEdit,
        getGroupLabel,
      ],
    );

    return (
      <Drawer
        placement="left"
        onClose={handleClose}
        open={open}
        width={300}
        closable={true}
        rootStyle={{ position: "absolute" }}
        getContainer={getContainer}
        styles={{
          body: {
            padding: 16,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
      >
        {renderContent}
      </Drawer>
    );
  }),
) as ChatSiderComponent;
